/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import {
  COLOR_MODE,
  DataMessage,
  operators,
  ImageDestaggerer,
  PointcloudNoiseSuppressor,
  PointcloudColorer,
  PointcloudScaler,
  PointcloudAutoLeveler,
  PrecomputedLut,
  RangeRGBData,
  ReturnColor,
  CHANNEL_FIELD,
} from '@ouster/webviz';
import { PointsDataMessage } from '@ouster/webviz/build/src/utils/profiles';
import {
  ColorPaletteName, // as OriginalColorPaletteName,
  PointCloudGlobalSettings,
  PointCloudSensorsSettings,
  ImageSettings,
  imageAspectRatio,
} from '@ouster/webviz-react-components';

import {
  action,
  thunk,
  Action as EzPzAction,
  Thunk as EzPzThunk,
  Computed,
  computed,
} from 'easy-peasy';
import { isUndefined } from 'lodash';
import type { Observable } from 'rxjs';
import {
  Subject,
  NEVER,
  merge,
  map,
  share,
  shareReplay,
  catchError,
  of,
  from,
  mergeMap,
  auditTime,
  animationFrameScheduler,
  filter,
  toArray,
  take,
} from 'rxjs';

import { isColorMode, SDKSensorInfoAugmented } from '../types';
import { applyPointCloudRgbAutoExposure } from './pointCloudRgbAutoExposure';
import {
  isReflectivityColorMode,
  isRgbColorMode,
  isWindowColorMode,
  shouldDisableAutoLevelsForMode,
} from './utils/vizColorModePolicyUtils';
import {
  findMessageForSelectedSensor,
  findSensorMetadataByIdentifier,
  resolveMetadataSensorIndexBySelection,
  resolveMetadataSensorKey,
} from './utils/vizSceneSensorSelectionUtils';
import { sensorMatchesId } from './utils/vizSensorUtils';
import { vizCameraModel } from './vizCamera';
import { vizErrorDefaultState, vizErrorModel } from './vizError';
import {
  buildAddedImageSettingsRecord,
  buildDeletedImageSettingsRecord,
  buildSetImageColorModeUpdatePayload,
  buildSetImageColorPaletteUpdatePayload,
  buildSwappedImageSettingsRecord,
  buildTwoDImageSettingsRuntimeState,
  buildUpdateImageStateChanges,
  clearImageColorerCache,
  DEFAULT_IMAGE_COLOR_MODES,
  DEFAULT_IMAGE_COLOR_PALETTES,
  defaultImageSettings,
  defaultTwoDImageSettings,
  initializeSelectedImageDefaultsForSensor,
  mergeTwoDImageSettingsState,
  selectEffectiveImageColorMode,
  selectEffectiveImageColorPalette,
  TwoDImageState as VizImageTwoDImageState,
} from './vizImage';
import { vizRendererDefaultState, vizRendererModel } from './vizRenderer';
import {
  allColorPalettes,
  twoDImageOptionsToAdd as twoDImageOptionsToAddBase,
  EXCLUDED_COLOR_MODES,
  EXCLUDED_IMAGE_COLOR_MODES,
} from '../utils/constants';
import {
  copyDataMessage,
  extractLidarProfileFromMetadata,
  getAvailableReturns,
  getPointCloudSensorsSettings,
  PROFILES,
} from '../utils/misc';
import {
  preloadRgbAutoExposureSdk,
  resetRgbAutoExposure,
} from '../utils/rgbAutoExposure';

export type TwoDImageState = VizImageTwoDImageState;

export const frameCache: { lastDataMessage: DataMessage[] | null } = {
  lastDataMessage: null,
};

const reprocessPointCloudFrame = (
  pointCloudReprocessed$: Subject<DataMessage[]>
) => {
  const lastData = frameCache.lastDataMessage;
  if (!lastData || lastData.length === 0) {
    return;
  }

  pointCloudReprocessed$.next(lastData);
};

/// ///////////////////////////////////////////////////////////////////////////
// Constants and helpers
/// ///////////////////////////////////////////////////////////////////////////
const MIN_POINT_SIZE = 1;
const DEFAULT_POINT_SIZE = 2;
const MAX_POINT_SIZE = 50;
const RGB_INIT_POINT_SIZE = 3;
// Increments should be integers because at the moent webviz does not
// seem to have anti-aliasing turned on for points (would have to look
// into GL_POINT_SMOOTH to do enable it)
const POINT_SIZE_INCREMENT = 1;

const CHANNEL_ORDER: ReadonlyArray<string> = [
  'RGB',
  'REFLECTIVITY',
  'REFLECTIVITY2',
  'NEAR_IR',
  'RANGE',
  'RANGE2',
  'SIGNAL',
  'SIGNAL2',
];

const RGB_MODE = String(COLOR_MODE.RGB);
const NEAR_IR_MODE = String(COLOR_MODE.NEAR_IR);
const SIGNAL_MODE = String(COLOR_MODE.SIGNAL);

const sortChannels = (channels: string[]): string[] => {
  const preferredSet = new Set(CHANNEL_ORDER);

  const preferred = channels
    .filter((ch) => preferredSet.has(ch))
    .sort((a, b) => CHANNEL_ORDER.indexOf(a) - CHANNEL_ORDER.indexOf(b));

  const custom = channels
    .filter((ch) => !preferredSet.has(ch))
    .sort((a, b) => a.localeCompare(b));

  return [...preferred, ...custom];
};

const DEFAULT_POINT_COLOR_MODES = CHANNEL_ORDER.filter(
  (mode) => mode !== RGB_MODE
);
const DEFAULT_POINT_COLOR_PALETTES = ['SPZ'];

const cloneMutablePointCloudChannels = (
  msg: PointsDataMessage,
  colorMode: COLOR_MODE | string,
  whichReturns: number[],
  isRgbMode: boolean
): PointsDataMessage => {
  if (isRgbMode || whichReturns.length === 0) {
    return msg;
  }

  const { channelFields: msgChannelFields } = msg;
  let channelFields = msgChannelFields;
  let changed = false;
  const mutableChannelNames = new Set<string>();

  // Resolve mutable channel name with the same return mapping semantics used
  // in pointcloud operators. NEAR_IR has no physical second-return channel, so
  // return 2 reuses NEAR_IR instead of a synthetic NEAR_IR2 key.
  const resolveMutableChannelName = (
    mode: COLOR_MODE | string,
    whichReturn: number
  ): string => {
    if (whichReturn === 0) {
      return mode;
    }

    if (mode === NEAR_IR_MODE) {
      return NEAR_IR_MODE;
    }

    return `${mode}${whichReturn + 1}`;
  };

  whichReturns.forEach((whichReturn) => {
    mutableChannelNames.add(resolveMutableChannelName(colorMode, whichReturn));
  });

  mutableChannelNames.forEach((channelName) => {
    const channelField = msgChannelFields[channelName];
    if (!channelField) {
      return;
    }

    if (!changed) {
      channelFields = { ...msgChannelFields };
      changed = true;
    }

    channelFields[channelName] = channelField.slice(0);
  });

  if (!changed) {
    return msg;
  }

  return {
    ...msg,
    channelFields,
  };
};

export function extractDefaultColorSetting<Type>(
  allColorSettings: Type[],
  defaultSettingValues: Type[],
  errorMsgIfNotLoaded: string
) {
  if (allColorSettings.length === 0) {
    throw new Error(errorMsgIfNotLoaded);
  }

  let defaultSetting = allColorSettings[0];
  // eslint-disable-next-line no-restricted-syntax
  for (const setting of defaultSettingValues) {
    if (allColorSettings.includes(setting)) {
      defaultSetting = setting;
      break;
    }
  }
  return defaultSetting;
}

// TODO (mw) Bring back color by sensor?
const COLOR_MODE_NAMES: Map<COLOR_MODE | string, string> = new Map([
  [COLOR_MODE.SIGNAL, 'Signal'],
  [COLOR_MODE.REFLECTIVITY, 'Reflectivity'],
  [COLOR_MODE.NEAR_IR, 'Near Infrared'],
  [COLOR_MODE.RANGE, 'Range'],
  // [COLOR_MODE.SENSOR, 'Sensor N°'],
]);
// --- Add Caches for Point Cloud Returns ---
let cachedSensorReturnNumbers: Record<string, number[]> = {};
let cachedColorerReturns: Record<string, ReturnColor[]> = {};

/**
 *
 * @param colorMode
 */
export function colorModeName(colorMode: COLOR_MODE | string) {
  return COLOR_MODE_NAMES.get(colorMode) || colorMode;
}

/// ///////////////////////////////////////////////////////////////////////////
// State and Model
/// ///////////////////////////////////////////////////////////////////////////

export type State = {
  // viz error
  hasVizError: boolean;
  vizErrorMessage: string;

  metadata: any; // todo type - Should be SensorMetadata[]
  sensorLuts: Record<string, PrecomputedLut>;
  pointCloudSensorsSettings: PointCloudSensorsSettings;
  pointCloudGlobalSettings: PointCloudGlobalSettings;

  // Pointcloud operators
  pcScaler: PointcloudScaler;
  pcAutoLeveler: PointcloudAutoLeveler;
  pcNoiseSuppressor: PointcloudNoiseSuppressor;
  pcColorers: Record<string, PointcloudColorer>;

  twoDImageSettings: TwoDImageState;

  // Image operators
  imageDestaggerer: ImageDestaggerer;

  // We do not need a pause stream emitter for replayed data
  // since we don't handle live data like in studio
  dataMessage: Subject<DataMessage[]>;
  pointCloudReprocessed$: Subject<DataMessage[]>;
  pointCloudObservable: Observable<RangeRGBData[][]>;
  imageObservable: Observable<DataMessage>;

  availableChannels: (CHANNEL_FIELD | string)[];
  channelsResolved: boolean;

  rgbChannels: string[];

  // Point Appearance
  pointSize: number;

  // TODO(pierre) on 2021-11-24: Maybe have one thunk which awaits for
  // the init to load and then sets everything at once to improve
  // performance?

  // Color Modes
  availablePointColorModes: (COLOR_MODE | string)[];
  availableImageColorModes: (COLOR_MODE | string)[];

  // Color Palettes
  colorPalettes: string[];

  modeSpecificPalettes: { [key in COLOR_MODE]?: ColorPaletteName };

  // 2D-image user select preference
  lastUserImageColorModeBySensorIdx: Record<
    string,
    Record<number, COLOR_MODE | string>
  >;
  lastUserImagePaletteBySensorIdxAndMode: Record<
    string,
    Record<number, Record<string, ColorPaletteName>>
  >;
  lastUserImageAutoLevelsBySensorIdx: Record<string, Record<number, boolean>>;
  lastUserImageNoiseSuppressionBySensorIdx: Record<
    string,
    Record<number, boolean>
  >;

  // Precedence: user preference > defaults, except forced-off (Reflectivity+CalRef or RGB-like).
  lastUserPointCloudAutoLevelsOn?: boolean;
  lastUserPointCloudNoiseSuppressionOn?: boolean;

  // 2D Image State
  is2DImageVisible: boolean;

  // Range Rings
  areRingsVisible: boolean;

  // sidebars
  // Unification sidebar
  leftSidebarExpanded: boolean;
  pointCloudFlipped: boolean;
  twoDImagesFlipped: boolean;

  // Gate cached re-emit after observable rebuild (disable while playing).
  shouldReplayCachedFrameOnRecreate: boolean;
};

// Canonical read selectors for nested image settings.
export const selectPointCloudGlobalSettings = (
  state: Pick<State, 'pointCloudGlobalSettings'>
): PointCloudGlobalSettings => state.pointCloudGlobalSettings;

export const selectPointCloudSensorsSettings = (
  state: Pick<State, 'pointCloudSensorsSettings'>
): PointCloudSensorsSettings => state.pointCloudSensorsSettings;

export const selectEffectivePointCloudColorMode = (
  state: Pick<State, 'pointCloudGlobalSettings'>
): COLOR_MODE | string => state.pointCloudGlobalSettings.coloringMode;

export const selectEffectivePointCloudColorPalette = (
  state: Pick<State, 'pointCloudGlobalSettings'>
): ColorPaletteName => state.pointCloudGlobalSettings.colorPalette;

export const selectEffectivePointCloudAutoLevelsOn = (
  state: Pick<State, 'pointCloudGlobalSettings'>
): boolean => state.pointCloudGlobalSettings.isAutoLevelsOn;

export const selectSelectedImagePanelSn = (
  state: Pick<State, 'twoDImageSettings'>
): string | null => state.twoDImageSettings.selectedImagePanelSn;

export const selectSelectedSensorImageSettings = (
  state: Pick<State, 'twoDImageSettings'>
): ImageSettings[] => {
  const selectedSn = state.twoDImageSettings.selectedImagePanelSn;
  if (!selectedSn) return [];

  return state.twoDImageSettings.imageSettings[selectedSn] ?? [];
};

export const defaultPointCloudSensorsSettings: PointCloudSensorsSettings = {};

export const defaultPointCloudGlobalSettings: PointCloudGlobalSettings = {
  coloringMode: COLOR_MODE.REFLECTIVITY,
  colorPalette: 'CalRef',
  colorPaletteOptions: Object.keys(allColorPalettes) as ColorPaletteName[],
  colorModeOptions: [],
  isAutoLevelsOn: true,
  isAutoLevelsDisabled: false,
  isNoiseSuppressionOn: false,
  isNoiseSuppressionDisabled: true, // Keep pc noise suppression disabled for preliminary unification
  showPointCloud: true,
  pointSize: DEFAULT_POINT_SIZE,
  showRangeRings: true,
  isOrthographicCamera: false,
};

export const defaultState: State = {
  ...vizErrorDefaultState,
  metadata: undefined, // Will become an array
  sensorLuts: {},
  pointCloudSensorsSettings: defaultPointCloudSensorsSettings,
  pointCloudGlobalSettings: defaultPointCloudGlobalSettings,

  dataMessage: new Subject<DataMessage[]>(),
  pointCloudReprocessed$: new Subject<DataMessage[]>(),
  pointCloudObservable: NEVER,

  availableChannels: [],
  channelsResolved: false,
  rgbChannels: [],

  // need to instantiate after data profile loads, doing reflectivity for now
  pcScaler: new PointcloudScaler(COLOR_MODE.REFLECTIVITY, false),
  pcAutoLeveler: new PointcloudAutoLeveler(COLOR_MODE.REFLECTIVITY, false),
  pcNoiseSuppressor: new PointcloudNoiseSuppressor(
    COLOR_MODE.REFLECTIVITY,
    false
  ),
  pcColorers: {},

  modeSpecificPalettes: {},

  lastUserImagePaletteBySensorIdxAndMode: {},
  lastUserImageColorModeBySensorIdx: {},
  lastUserImageAutoLevelsBySensorIdx: {},
  lastUserImageNoiseSuppressionBySensorIdx: {},

  lastUserPointCloudAutoLevelsOn: undefined,
  lastUserPointCloudNoiseSuppressionOn: undefined,

  imageObservable: NEVER,
  twoDImageSettings: defaultTwoDImageSettings,
  imageDestaggerer: new ImageDestaggerer([]),

  pointSize: DEFAULT_POINT_SIZE,
  // Populated from incoming data message during channel initialization.
  availablePointColorModes: [],
  availableImageColorModes: [],
  colorPalettes: [],
  ...vizRendererDefaultState,
  shouldReplayCachedFrameOnRecreate: true,
};

type PointCloudSyncState = {
  pcColorers: Record<
    string,
    {
      channelField: COLOR_MODE | string;
      isRgb: boolean;
      colorMap: unknown;
    }
  >;
  pcAutoLeveler: { channelField: COLOR_MODE | string; isActive: boolean };
  pcScaler: { isActive: boolean; channelField: COLOR_MODE | string };
  pcNoiseSuppressor: {
    channelField: COLOR_MODE | string;
    isActive: boolean;
  };
  lastUserPointCloudAutoLevelsOn?: boolean;
  lastUserPointCloudNoiseSuppressionOn?: boolean;
  modeSpecificPalettes: { [key in COLOR_MODE]?: ColorPaletteName };
};

function updateAllPointColorersColorMap(
  pcColorers: PointCloudSyncState['pcColorers'],
  palette: ColorPaletteName
) {
  Object.values(pcColorers).forEach((colorer) => {
    colorer.colorMap = allColorPalettes[palette];
  });
}

function syncNoiseSuppressionForMode(
  state: PointCloudSyncState,
  oldPointCloudGlobalSettings: PointCloudGlobalSettings,
  coloringMode: COLOR_MODE | string
) {
  const isNearIrMode = coloringMode === NEAR_IR_MODE;
  const userPref = state.lastUserPointCloudNoiseSuppressionOn;
  oldPointCloudGlobalSettings.isNoiseSuppressionDisabled = !isNearIrMode;
  oldPointCloudGlobalSettings.isNoiseSuppressionOn = isNearIrMode
    ? userPref ?? true
    : false;
  state.pcNoiseSuppressor.channelField = coloringMode;
  state.pcNoiseSuppressor.isActive =
    oldPointCloudGlobalSettings.isNoiseSuppressionOn;
}

function applyRgbModeSettings(
  state: PointCloudSyncState,
  oldPointCloudGlobalSettings: PointCloudGlobalSettings
) {
  const userPref = state.lastUserPointCloudAutoLevelsOn;
  oldPointCloudGlobalSettings.isAutoLevelsDisabled = false;
  oldPointCloudGlobalSettings.isAutoLevelsOn = userPref ?? true;
  oldPointCloudGlobalSettings.isNoiseSuppressionDisabled = true;
  oldPointCloudGlobalSettings.isNoiseSuppressionOn = false;
  // For RGB, the toggle controls SDK RGB auto-exposure only.
  // Keep point-cloud auto-level/scaler operators disabled.
  state.pcAutoLeveler.isActive = false;
  state.pcScaler.isActive = false;
  state.pcNoiseSuppressor.isActive = false;

  Object.values(state.pcColorers).forEach((colorer) => {
    colorer.colorMap =
      allColorPalettes['RGB' as ColorPaletteName] ?? colorer.colorMap;
  });

  if (!oldPointCloudGlobalSettings.colorPaletteOptions.includes('RGB')) {
    oldPointCloudGlobalSettings.colorPaletteOptions = [
      ...oldPointCloudGlobalSettings.colorPaletteOptions,
      'RGB',
    ];
  }
  oldPointCloudGlobalSettings.colorPalette = 'RGB';
}

function applyNonReflectivityModeSettings(
  state: PointCloudSyncState,
  oldPointCloudGlobalSettings: PointCloudGlobalSettings,
  coloringMode: COLOR_MODE | string
) {
  oldPointCloudGlobalSettings.isAutoLevelsDisabled = false;
  const userPref = state.lastUserPointCloudAutoLevelsOn;
  oldPointCloudGlobalSettings.isAutoLevelsOn = userPref ?? true;
  state.pcAutoLeveler.isActive = oldPointCloudGlobalSettings.isAutoLevelsOn;

  const paletteToRestore = isColorMode(coloringMode)
    ? state.modeSpecificPalettes[coloringMode as COLOR_MODE] ?? 'Ouster'
    : ('Ouster' as ColorPaletteName);
  oldPointCloudGlobalSettings.colorPalette = paletteToRestore;
  updateAllPointColorersColorMap(state.pcColorers, paletteToRestore);
  state.pcScaler.channelField = coloringMode;
}

function applyWindowModeSettings(
  state: PointCloudSyncState,
  oldPointCloudGlobalSettings: PointCloudGlobalSettings,
  coloringMode: COLOR_MODE | string
) {
  // Unlike Reflectivity+CalRef, WINDOW disables AE unconditionally (no
  // palette/calibration gate) - see SensorModel._create_view_mode_for_field
  // in the SDK's viz/model.py.
  oldPointCloudGlobalSettings.isAutoLevelsDisabled = true;
  oldPointCloudGlobalSettings.isAutoLevelsOn = false;
  state.pcAutoLeveler.isActive = false;

  const paletteToRestore = isColorMode(coloringMode)
    ? state.modeSpecificPalettes[coloringMode as COLOR_MODE] ?? 'Ouster'
    : ('Ouster' as ColorPaletteName);
  oldPointCloudGlobalSettings.colorPalette = paletteToRestore;
  updateAllPointColorersColorMap(state.pcColorers, paletteToRestore);
  state.pcScaler.channelField = coloringMode;
  // No scalingFactors entry exists for WINDOW in @ouster/webviz, so keep the
  // scaler inactive rather than scaling by an undefined factor.
  state.pcScaler.isActive = false;
}

function applyReflectivityModeSettings(
  state: PointCloudSyncState,
  oldPointCloudGlobalSettings: PointCloudGlobalSettings,
  coloringMode: COLOR_MODE | string,
  currentMode: COLOR_MODE | string,
  currentPalette: ColorPaletteName
) {
  if (!isReflectivityColorMode(currentMode) && currentPalette) {
    if (isColorMode(currentMode)) {
      state.modeSpecificPalettes[currentMode as COLOR_MODE] = currentPalette;
    }
  }

  const rememberedPalette =
    isColorMode(coloringMode) &&
    state.modeSpecificPalettes[coloringMode as COLOR_MODE]
      ? state.modeSpecificPalettes[coloringMode as COLOR_MODE]
      : undefined;
  const nextPalette = rememberedPalette ?? 'CalRef';
  oldPointCloudGlobalSettings.colorPalette = nextPalette;

  const isCalRefReflectivity = nextPalette === ('CalRef' as ColorPaletteName);
  oldPointCloudGlobalSettings.isAutoLevelsDisabled = isCalRefReflectivity;
  oldPointCloudGlobalSettings.isAutoLevelsOn = isCalRefReflectivity
    ? false
    : state.lastUserPointCloudAutoLevelsOn ?? true;
  state.pcAutoLeveler.isActive = oldPointCloudGlobalSettings.isAutoLevelsOn;

  updateAllPointColorersColorMap(state.pcColorers, nextPalette);
  state.pcScaler.isActive = true;
}

function syncOperatorsToColorMode({
  state,
  oldPointCloudGlobalSettings,
  coloringMode,
  currentMode,
  currentPalette,
  rgbChannels,
}: {
  state: PointCloudSyncState;
  oldPointCloudGlobalSettings: PointCloudGlobalSettings;
  coloringMode: COLOR_MODE | string;
  currentMode: COLOR_MODE | string;
  currentPalette: ColorPaletteName;
  rgbChannels: string[];
}) {
  Object.values(state.pcColorers).forEach((colorer) => {
    colorer.channelField = coloringMode;
    colorer.isRgb = rgbChannels.includes(coloringMode);
  });
  state.pcAutoLeveler.channelField = coloringMode;

  const isRgbMode = isRgbColorMode({ coloringMode, rgbChannels });
  if (isRgbMode) {
    applyRgbModeSettings(state, oldPointCloudGlobalSettings);
    syncNoiseSuppressionForMode(
      state,
      oldPointCloudGlobalSettings,
      coloringMode
    );
    return;
  }

  // Remove RGB palette option if present
  oldPointCloudGlobalSettings.colorPaletteOptions =
    oldPointCloudGlobalSettings.colorPaletteOptions.filter(
      (palette) => palette !== ('RGB' as ColorPaletteName)
    );

  const isReflectivityMode = isReflectivityColorMode(coloringMode);
  if (isReflectivityMode) {
    applyReflectivityModeSettings(
      state,
      oldPointCloudGlobalSettings,
      coloringMode,
      currentMode,
      currentPalette
    );
  } else if (isWindowColorMode(coloringMode)) {
    applyWindowModeSettings(state, oldPointCloudGlobalSettings, coloringMode);
  } else {
    applyNonReflectivityModeSettings(
      state,
      oldPointCloudGlobalSettings,
      coloringMode
    );
  }

  syncNoiseSuppressionForMode(state, oldPointCloudGlobalSettings, coloringMode);
}

function syncOperatorsToColorPalette({
  state,
  oldPointCloudGlobalSettings,
  currentMode,
  newPalette,
}: {
  state: Pick<
    PointCloudSyncState,
    | 'pcColorers'
    | 'pcAutoLeveler'
    | 'modeSpecificPalettes'
    | 'lastUserPointCloudAutoLevelsOn'
  >;
  oldPointCloudGlobalSettings: PointCloudGlobalSettings;
  currentMode: COLOR_MODE | string;
  newPalette: ColorPaletteName;
}) {
  const isCalRefReflectivity = shouldDisableAutoLevelsForMode({
    coloringMode: oldPointCloudGlobalSettings.coloringMode,
    colorPalette: newPalette,
    rgbChannels: [],
  });
  const userPref = state.lastUserPointCloudAutoLevelsOn;

  oldPointCloudGlobalSettings.isAutoLevelsDisabled = isCalRefReflectivity;
  oldPointCloudGlobalSettings.isAutoLevelsOn = isCalRefReflectivity
    ? false
    : userPref ?? true;
  state.pcAutoLeveler.isActive = oldPointCloudGlobalSettings.isAutoLevelsOn;

  oldPointCloudGlobalSettings.colorPalette = newPalette;
  if (isColorMode(currentMode) && newPalette !== 'RGB') {
    state.modeSpecificPalettes[currentMode as COLOR_MODE] = newPalette;
  }

  updateAllPointColorersColorMap(state.pcColorers, newPalette);
}

/**
 * TODOS:
 * Eliminate point shape toggling
 */
export interface VizSceneStateModel extends State {
  setHasVizError: EzPzAction<VizSceneStateModel, boolean>;
  setVizErrorMessage: EzPzAction<VizSceneStateModel, string>;
  // Unification - state for left sidebar component
  _setMetadata: EzPzAction<VizSceneStateModel, any>; // Payload is SensorMetadata[]
  updateMetadata: EzPzThunk<VizSceneStateModel, SDKSensorInfoAugmented[]>; // Payload is SensorMetadata[]
  updateSensorLuts: EzPzAction<
    VizSceneStateModel,
    Record<string, PrecomputedLut>
  >;
  _setPcColorers: EzPzAction<
    VizSceneStateModel,
    Record<string, PointcloudColorer>
  >;
  recreateObservables: EzPzThunk<VizSceneStateModel>;
  _setPointCloudGlobalSettings: EzPzAction<
    VizSceneStateModel,
    Partial<PointCloudGlobalSettings>
  >;
  updatePointCloudGlobalSettings: EzPzThunk<
    VizSceneStateModel,
    Partial<PointCloudGlobalSettings>
  >;

  _setPointCloudSensorsSettings: EzPzAction<
    VizSceneStateModel,
    PointCloudSensorsSettings
  >;
  // Payload is [sensorSerialNumber, partialSettingsForThatSensor]
  updatePointCloudSensorsSettings: EzPzThunk<
    VizSceneStateModel,
    [string, Partial<PointCloudSensorsSettings[string]>]
  >;

  _setTwoDImageSettings: EzPzAction<
    VizSceneStateModel,
    Partial<TwoDImageState>
  >;

  updateTwoDImageSettings: EzPzThunk<
    VizSceneStateModel,
    Partial<TwoDImageState>
  >;
  swapImages: EzPzThunk<
    VizSceneStateModel,
    { sensorSn: string; indexA: number; indexB: number }
  >;
  addImage: EzPzThunk<VizSceneStateModel, string>;
  deleteImage: EzPzThunk<
    VizSceneStateModel,
    { sensorSn: string; index: number }
  >;
  updateImage: EzPzThunk<
    VizSceneStateModel,
    {
      sensorSn: string;
      index: number;
      partialSettings: Partial<ImageSettings>;
      preserveExplicitToggles?: boolean;
      markColorModeDirty?: boolean;
    }
  >;
  togglePointCloudVisibility: EzPzThunk<VizSceneStateModel>;

  _setSelectedImagePanelSn: EzPzAction<VizSceneStateModel, string | null>;
  setSelectedImagePanelSn: EzPzThunk<VizSceneStateModel, string | null>;
  currentSensorImageSettings: Computed<VizSceneStateModel, ImageSettings[]>;

  // Data message subject and observables
  _setDataMessage: EzPzAction<VizSceneStateModel, Subject<DataMessage[]>>;
  _setPointCloudObservable: EzPzAction<
    VizSceneStateModel,
    Observable<RangeRGBData[][]>
  >;
  _setImageObservable: EzPzAction<VizSceneStateModel, Observable<DataMessage>>;
  updateDataMessage: EzPzThunk<VizSceneStateModel, Subject<DataMessage[]>>; // Create pausable and observables

  // Cameras
  toggleCameraOrthographicMode: EzPzThunk<VizSceneStateModel>;
  setIsCameraOrthographic: EzPzAction<VizSceneStateModel, boolean>;

  // Point Appearance
  incrementPointSize: EzPzThunk<VizSceneStateModel>;
  decrementPointSize: EzPzThunk<VizSceneStateModel>;
  setPointSize: EzPzAction<VizSceneStateModel, number>;

  // Color Modes
  setAvailablePointColorModes: EzPzThunk<
    VizSceneStateModel,
    (COLOR_MODE | string)[]
  >;
  setAvailableImageColorModes: EzPzThunk<
    VizSceneStateModel,
    (COLOR_MODE | string)[]
  >;
  _setAvailablePointColorModes: EzPzAction<
    VizSceneStateModel,
    (COLOR_MODE | string)[]
  >;
  _setAvailableImageColorModes: EzPzAction<
    VizSceneStateModel,
    (COLOR_MODE | string)[]
  >;
  setDefaultPointColorMode: EzPzThunk<VizSceneStateModel>;
  setDefaultImageColorMode: EzPzThunk<VizSceneStateModel>;
  setPointColorMode: EzPzThunk<VizSceneStateModel, COLOR_MODE | string>;
  setImageColorMode: EzPzThunk<VizSceneStateModel, COLOR_MODE | string>;
  togglePointCloudAutoLevel: EzPzThunk<VizSceneStateModel>;

  // Color Palettes
  setColorPalettes: EzPzThunk<VizSceneStateModel, string[]>;
  _setColorPalettes: EzPzAction<VizSceneStateModel, string[]>;
  setDefaultPointColorPalette: EzPzThunk<VizSceneStateModel>;
  setDefaultImageColorPalette: EzPzThunk<VizSceneStateModel>;
  setPointColorPalette: EzPzThunk<VizSceneStateModel, string>;
  setImageColorPalette: EzPzThunk<VizSceneStateModel, string>;

  _setLastUserImagePaletteBySensorIdxAndMode: EzPzAction<
    VizSceneStateModel,
    State['lastUserImagePaletteBySensorIdxAndMode']
  >;
  _setLastUserImageColorModeBySensorIdx: EzPzAction<
    VizSceneStateModel,
    State['lastUserImageColorModeBySensorIdx']
  >;
  _setLastUserImageAutoLevelsBySensorIdx: EzPzAction<
    VizSceneStateModel,
    State['lastUserImageAutoLevelsBySensorIdx']
  >;
  _setLastUserImageNoiseSuppressionBySensorIdx: EzPzAction<
    VizSceneStateModel,
    State['lastUserImageNoiseSuppressionBySensorIdx']
  >;

  // 2D Image State
  toggle2DImageVisibility: EzPzThunk<VizSceneStateModel>;
  setIs2DImageVisible: EzPzAction<VizSceneStateModel, boolean>;

  // Range Rings
  toggleRangeRingsVisibility: EzPzThunk<VizSceneStateModel>;
  setAreRangeRingsVisible: EzPzAction<VizSceneStateModel, boolean>;

  // Angle Rulers
  toggleAngleRulersVisibility: EzPzThunk<VizSceneStateModel>;
  setAreAngleRulersVisible: EzPzAction<VizSceneStateModel, boolean>;

  setLeftSidebarExpanded: EzPzAction<VizSceneStateModel, boolean>;
  setPointCloudFlipped: EzPzAction<VizSceneStateModel, boolean>;
  setTwoDImagesFlipped: EzPzAction<VizSceneStateModel, boolean>;
  setShouldReplayCachedFrameOnRecreate: EzPzAction<VizSceneStateModel, boolean>;

  // Init available channels
  initAvailableChannels: EzPzThunk<VizSceneStateModel>;
  setAvailableChannels: EzPzAction<
    VizSceneStateModel,
    (CHANNEL_FIELD | string)[]
  >;
  setChannelsResolved: EzPzAction<VizSceneStateModel, boolean>;

  // RGB channels
  setRgbChannels: EzPzAction<VizSceneStateModel, string[]>;
}

export function resolveOrderedModes(
  availableChannels: (CHANNEL_FIELD | string)[]
) {
  const validPointModesRaw = availableChannels.filter(
    (m) => !EXCLUDED_COLOR_MODES.includes(m as any)
  );
  const rgbAvailable = validPointModesRaw.includes(COLOR_MODE.RGB);
  const validPointModes = rgbAvailable
    ? validPointModesRaw
    : validPointModesRaw.filter((m) => m !== (COLOR_MODE.RGB as any));
  const orderedValidPointModes = sortChannels(validPointModes);

  const validImageModesRaw = availableChannels.filter(
    (m) => !EXCLUDED_IMAGE_COLOR_MODES.includes(m as any)
  );
  const orderedValidImageModes = sortChannels(validImageModesRaw);

  return { orderedValidPointModes, orderedValidImageModes, rgbAvailable };
}

type InitAvailableChannelsActions = {
  setAvailableChannels: (channels: (CHANNEL_FIELD | string)[]) => void;
  setRgbChannels: (channels: string[]) => void;
  setAvailablePointColorModes: (modes: (COLOR_MODE | string)[]) => void;
  updatePointCloudGlobalSettings: (
    settings: Partial<PointCloudGlobalSettings>
  ) => void;
  setAvailableImageColorModes: (modes: (COLOR_MODE | string)[]) => void;
  _setPointCloudSensorsSettings: (settings: PointCloudSensorsSettings) => void;
  _setPcColorers: (pcColorers: Record<string, PointcloudColorer>) => void;
  updateTwoDImageSettings: (settings: Partial<TwoDImageState>) => void;
  updateDataMessage: (dataMessage: Subject<DataMessage[]>) => void;
  setChannelsResolved: (resolved: boolean) => void;
};

type InitAvailableChannelsState = {
  metadata: any;
  pointCloudGlobalSettings: {
    colorPaletteOptions: ColorPaletteName[];
    coloringMode: COLOR_MODE | string;
    pointSize: number;
    showRangeRings: boolean;
  };
  pointCloudSensorsSettings: PointCloudSensorsSettings;
  pcAutoLeveler: { isActive: boolean };
  pcScaler: { isActive: boolean };
  pcNoiseSuppressor: { isActive: boolean };
  pcColorers: Record<string, PointcloudColorer>;
  twoDImageSettings: {
    selectedImagePanelSn: string | null;
    imageSettings: Record<string, ImageSettings[]>;
  };
  lastUserImageColorModeBySensorIdx: Record<
    string,
    Record<number, COLOR_MODE | string>
  >;
  dataMessage: unknown;
  pointCloudObservable: unknown;
};

export function resolveInitialPointMode({
  prevMode,
  orderedValidPointModes,
  rgbAvailable,
  rgbChannels,
}: {
  prevMode: string;
  orderedValidPointModes: string[];
  rgbAvailable: boolean;
  rgbChannels: string[];
}): string | undefined {
  if (rgbAvailable) return COLOR_MODE.RGB;
  if (rgbChannels.length > 0) {
    const [firstRgbChannel] = rgbChannels;
    return firstRgbChannel;
  }
  if (prevMode && orderedValidPointModes.includes(prevMode)) return prevMode;
  if (orderedValidPointModes.includes(COLOR_MODE.REFLECTIVITY)) {
    return COLOR_MODE.REFLECTIVITY;
  }
  const [firstOrderedMode] = orderedValidPointModes;
  return firstOrderedMode;
}

function resolveProfileChannels(profile?: string): string[] {
  if (!profile || !PROFILES[profile]) {
    return [];
  }

  return sortChannels(Object.keys(PROFILES[profile]));
}

function resolveAvailableImageColorModesForProfile(profile?: string) {
  return resolveProfileChannels(profile).filter(
    (m) => !EXCLUDED_IMAGE_COLOR_MODES.includes(m as any)
  );
}

function resolvePointcloudLookupKey({
  messageSn,
  state,
}: {
  messageSn: string;
  state: Pick<State, 'metadata' | 'pointCloudSensorsSettings' | 'pcColorers'>;
}): string {
  if (
    state.pointCloudSensorsSettings[messageSn] ||
    state.pcColorers[messageSn] ||
    cachedSensorReturnNumbers[messageSn] ||
    cachedColorerReturns[messageSn]
  ) {
    return messageSn;
  }

  const metadata = Array.isArray(state.metadata)
    ? (state.metadata as (SDKSensorInfoAugmented & { productSn?: string })[])
    : [];
  const matchedMetadata = metadata.find((meta) =>
    sensorMatchesId(meta, messageSn)
  );

  return matchedMetadata
    ? resolveMetadataSensorKey(matchedMetadata)
    : messageSn;
}

function resolveSensorChannelsForReturns({
  sensorKey,
  state,
  fallbackAvailableChannels,
}: {
  sensorKey: string;
  state:
    | Pick<State, 'metadata' | 'pointCloudSensorsSettings' | 'pcColorers'>
    | InitAvailableChannelsState;
  fallbackAvailableChannels: (CHANNEL_FIELD | string)[];
}): (CHANNEL_FIELD | string)[] {
  const messages = frameCache.lastDataMessage;
  if (!Array.isArray(messages) || messages.length === 0) {
    return fallbackAvailableChannels;
  }

  const directMatch = messages.find((msg) => msg?.sensorInfo?.sn === sensorKey);
  if (directMatch?.availableChannels?.length) {
    return directMatch.availableChannels;
  }

  const metadata = Array.isArray(state.metadata)
    ? (state.metadata as (SDKSensorInfoAugmented & { productSn?: string })[])
    : [];
  const matchedMetadata = metadata.find((meta) =>
    sensorMatchesId(meta, sensorKey)
  );

  if (matchedMetadata) {
    const metadataMessage = messages.find((msg) =>
      sensorMatchesId(matchedMetadata, msg?.sensorInfo?.sn ?? '')
    );
    if (metadataMessage?.availableChannels?.length) {
      return metadataMessage.availableChannels;
    }
  }

  const lookupMatch = messages.find((msg) => {
    const messageSn = msg?.sensorInfo?.sn;
    if (!messageSn) return false;
    return (
      resolvePointcloudLookupKey({
        messageSn,
        state: state,
      }) === sensorKey
    );
  });

  return lookupMatch?.availableChannels?.length
    ? lookupMatch.availableChannels
    : fallbackAvailableChannels;
}

function resolveAvailableImageColorModesForSelectedSensor({
  selectedSn,
  sensorsMetadata,
}: {
  selectedSn: string | null;
  sensorsMetadata: SDKSensorInfoAugmented[];
}) {
  if (selectedSn && Array.isArray(frameCache.lastDataMessage)) {
    const selectedSensorMessage = findMessageForSelectedSensor({
      messages: frameCache.lastDataMessage,
      selectedSn,
      sensorsMetadata,
    });

    if (selectedSensorMessage?.availableChannels?.length) {
      const { orderedValidImageModes } = resolveOrderedModes(
        selectedSensorMessage.availableChannels
      );
      return orderedValidImageModes;
    }
  }

  const selectedMetadataIndex = resolveMetadataSensorIndexBySelection(
    sensorsMetadata,
    selectedSn
  );
  const selectedSensorMetadata =
    selectedMetadataIndex >= 0 ? sensorsMetadata[selectedMetadataIndex] : null;
  const selectedSensorProfile = selectedSensorMetadata
    ? extractLidarProfileFromMetadata(selectedSensorMetadata)
    : undefined;
  return resolveAvailableImageColorModesForProfile(selectedSensorProfile);
}

function resolveAvailablePointColorModesUnionForMetadata(
  sensorsMetadata: SDKSensorInfoAugmented[]
) {
  const unionChannels = new Set<string>();

  sensorsMetadata.forEach((meta) => {
    const profile = extractLidarProfileFromMetadata(meta);
    resolveProfileChannels(profile).forEach((channel) => {
      unionChannels.add(channel);
    });
  });

  return sortChannels(Array.from(unionChannels)).filter(
    (m) => !EXCLUDED_COLOR_MODES.includes(m as any)
  );
}

function resolveInitialPointPalette(
  chosenMode: string,
  rgbChannels: string[]
): ColorPaletteName {
  if (isRgbColorMode({ coloringMode: chosenMode, rgbChannels })) {
    return 'RGB';
  }
  if (isReflectivityColorMode(chosenMode)) {
    return 'CalRef';
  }
  return 'Ouster';
}

function resolvePointReturnsModeForSensor({
  coloringMode,
  sensorChannels,
  rgbChannels,
}: {
  coloringMode: COLOR_MODE | string;
  sensorChannels: (CHANNEL_FIELD | string)[];
  rgbChannels: string[];
}): COLOR_MODE | string {
  const requestedReturns = getAvailableReturns({
    colorMode: String(coloringMode),
    actualChannels: sensorChannels,
  });
  if (requestedReturns.length > 0) {
    return coloringMode;
  }

  const requestedRgbMode =
    coloringMode === RGB_MODE ||
    isRgbColorMode({
      coloringMode,
      rgbChannels,
    });

  if (!requestedRgbMode) {
    return coloringMode;
  }

  const fallbackModes: Array<COLOR_MODE | string> = [
    COLOR_MODE.RANGE,
    COLOR_MODE.REFLECTIVITY,
    COLOR_MODE.SIGNAL,
    COLOR_MODE.NEAR_IR,
  ];

  const fallbackMode = fallbackModes.find(
    (mode) =>
      getAvailableReturns({
        colorMode: String(mode),
        actualChannels: sensorChannels,
      }).length > 0
  );

  return fallbackMode ?? coloringMode;
}

function updatePointColorPaletteOptions(
  actions: InitAvailableChannelsActions,
  getState: () => InitAvailableChannelsState,
  rgbChannels: string[]
) {
  const withRgbPalettes = Array.from(
    new Set([...getState().pointCloudGlobalSettings.colorPaletteOptions, 'RGB'])
  );
  const withoutRgbPalettes =
    getState().pointCloudGlobalSettings.colorPaletteOptions.filter(
      (p) => p !== 'RGB'
    );

  actions.updatePointCloudGlobalSettings({
    colorPaletteOptions: (rgbChannels.length > 0
      ? withRgbPalettes
      : withoutRgbPalettes) as ColorPaletteName[],
  });
}

function initializePointCloudDefaults({
  actions,
  getState,
  availableChannels,
  rgbChannels,
  chosenMode,
  chosenPalette,
}: {
  actions: InitAvailableChannelsActions;
  getState: () => InitAvailableChannelsState;
  availableChannels: (CHANNEL_FIELD | string)[];
  rgbChannels: string[];
  chosenMode: string;
  chosenPalette: ColorPaletteName;
}) {
  actions.updatePointCloudGlobalSettings({ coloringMode: chosenMode });
  actions.updatePointCloudGlobalSettings({ colorPalette: chosenPalette });

  const initialIsRgb = isRgbColorMode({
    coloringMode: chosenMode,
    rgbChannels,
  });

  // Preserve any pre-applied point size (for example from URL scene state).
  // Only apply the RGB default when point size is still at the baseline default.
  if (
    initialIsRgb &&
    getState().pointCloudGlobalSettings.pointSize === DEFAULT_POINT_SIZE
  ) {
    actions.updatePointCloudGlobalSettings({
      pointSize: RGB_INIT_POINT_SIZE,
    });
  }

  const forcedAutoOff = shouldDisableAutoLevelsForMode({
    coloringMode: chosenMode,
    colorPalette: chosenPalette,
    rgbChannels,
  });

  const firstAuto = !forcedAutoOff;
  const firstScaler = !(
    isReflectivityColorMode(chosenMode) ||
    isRgbColorMode({ coloringMode: chosenMode, rgbChannels })
  );
  const firstNoise = chosenMode === NEAR_IR_MODE;

  actions.updatePointCloudGlobalSettings({ isAutoLevelsOn: firstAuto });
  actions.updatePointCloudGlobalSettings({
    isAutoLevelsDisabled: forcedAutoOff,
  });

  // Keep operator in sync with disabled/off state
  getState().pcAutoLeveler.isActive = firstAuto;

  const currentState = getState();
  const pcSensorsSettings = { ...currentState.pointCloudSensorsSettings };
  Object.keys(pcSensorsSettings).forEach((sn) => {
    const sensorChannels = resolveSensorChannelsForReturns({
      sensorKey: sn,
      state: currentState,
      fallbackAvailableChannels: availableChannels,
    });
    const returnsMode = resolvePointReturnsModeForSensor({
      coloringMode: chosenMode,
      sensorChannels,
      rgbChannels,
    });
    const sensorSettings = getPointCloudSensorsSettings(
      returnsMode,
      sn,
      sensorChannels
    );
    const returnsSettings = sensorSettings[sn]?.returnsSettings ?? [];

    pcSensorsSettings[sn].returnsSettings = returnsSettings;

    cachedSensorReturnNumbers[sn] = returnsSettings.map((_ret, idx) => idx);
    cachedColorerReturns[sn] = returnsSettings.map((_ret, idx) => ({
      index: idx,
      highlightColor: null,
    }));
  });
  actions._setPointCloudSensorsSettings(pcSensorsSettings);

  getState().pcAutoLeveler = new PointcloudAutoLeveler(chosenMode, firstAuto);
  getState().pcScaler = new PointcloudScaler(chosenMode, firstScaler);
  getState().pcNoiseSuppressor = new PointcloudNoiseSuppressor(
    chosenMode,
    firstNoise
  );

  const paletteKey = chosenPalette;
  const colorMapToUse =
    allColorPalettes[paletteKey] ??
    (paletteKey === 'RGB' ? allColorPalettes.RGB : allColorPalettes.Ouster);
  const seeded = { ...getState().pcColorers };
  const isRgb = isRgbColorMode({
    coloringMode: chosenMode,
    rgbChannels,
  });
  Object.keys(pcSensorsSettings).forEach((sn) => {
    const col =
      seeded[sn] ?? new PointcloudColorer(chosenMode, colorMapToUse, isRgb);
    col.channelField = chosenMode;
    col.colorMap = colorMapToUse;
    col.whichReturns = cachedColorerReturns[sn]?.length
      ? cachedColorerReturns[sn]
      : [{ index: 0, highlightColor: null }];
    seeded[sn] = col;
  });
  actions._setPcColorers(seeded);
}

function maybeFlipAutoAfterFirstFrame(
  actions: InitAvailableChannelsActions,
  getState: () => InitAvailableChannelsState,
  chosenMode: string,
  chosenPalette: ColorPaletteName,
  rgbChannels: string[]
) {
  const shouldFlipAuto = !shouldDisableAutoLevelsForMode({
    coloringMode: chosenMode,
    colorPalette: chosenPalette,
    rgbChannels,
  });
  if (!shouldFlipAuto) return;

  const pointCloudObservable = getState().pointCloudObservable as
    | Observable<RangeRGBData[][]>
    | undefined;
  const once = pointCloudObservable?.pipe(take(1));
  if (!once) return;

  const flip = once.subscribe({
    next: () => {
      const st = getState();
      // Re-derive from the current mode/desired setting, not the `chosenMode`
      // this function was armed with — by the time the first frame fires, a
      // caller (e.g. the URL scene-restore flow) may have already changed the
      // coloring mode and/or explicitly chosen isAutoLevelsOn, and clobbering
      // those with stale/hardcoded values here would silently override them.
      const currentMode = st.pointCloudGlobalSettings.coloringMode;
      const desiredIsAutoLevelsOn = Boolean(
        (st.pointCloudGlobalSettings as { isAutoLevelsOn?: boolean })
          .isAutoLevelsOn
      );
      const isRgbMode = isRgbColorMode({
        coloringMode: currentMode,
        rgbChannels,
      });
      if (isRgbMode) {
        st.pcAutoLeveler.isActive = false;
        st.pcScaler.isActive = false;
      } else {
        st.pcAutoLeveler.isActive = desiredIsAutoLevelsOn;
        if (desiredIsAutoLevelsOn && !isReflectivityColorMode(currentMode)) {
          st.pcScaler.isActive = false;
        }
      }
      actions.updateDataMessage(st.dataMessage as Subject<DataMessage[]>);
      flip.unsubscribe();
    },
  });
}

function initializeAvailableChannelsFromMessage(
  actions: InitAvailableChannelsActions,
  getState: () => InitAvailableChannelsState,
  message: DataMessage
) {
  const availableChannels = message.availableChannels || [];
  actions.setAvailableChannels(availableChannels);

  const rgbChannels = message.rgbChannels || [];
  actions.setRgbChannels(rgbChannels);

  const { orderedValidPointModes, orderedValidImageModes, rgbAvailable } =
    resolveOrderedModes(availableChannels);

  actions.setAvailablePointColorModes(orderedValidPointModes);
  actions.updatePointCloudGlobalSettings({
    colorModeOptions: orderedValidPointModes,
  });
  actions.setAvailableImageColorModes(orderedValidImageModes);

  updatePointColorPaletteOptions(actions, getState, rgbChannels);

  const prevMode = getState().pointCloudGlobalSettings.coloringMode;
  // orderedValidPointModes can't be empty here — setAvailablePointColorModes
  // above already throws "No available point color modes yet" in that case —
  // so resolveInitialPointMode always returns a truthy mode.
  const chosenMode = resolveInitialPointMode({
    prevMode,
    orderedValidPointModes,
    rgbAvailable,
    rgbChannels,
  })!;
  const chosenPalette = resolveInitialPointPalette(chosenMode, rgbChannels);

  initializePointCloudDefaults({
    actions,
    getState,
    availableChannels,
    rgbChannels,
    chosenMode,
    chosenPalette,
  });

  const {
    twoDImageSettings: {
      selectedImagePanelSn,
      imageSettings: currentImageSettings,
    },
  } = getState();
  if (selectedImagePanelSn) {
    const initializedImageSettings = initializeSelectedImageDefaultsForSensor({
      currentImageSettings,
      selectedImagePanelSn,
      orderedValidImageModes,
      rgbChannels,
      rgbAvailable,
      lastUserImageColorModeBySensorIdx:
        getState().lastUserImageColorModeBySensorIdx,
    });

    if (initializedImageSettings) {
      // Rebuild the image observable first so that buildTwoDImageSettingsRuntimeState
      // wires the per-panel observables to the freshly-created imgObservable.
      actions.updateDataMessage(
        getState().dataMessage as Subject<DataMessage[]>
      );
      actions.updateTwoDImageSettings({
        imageSettings: initializedImageSettings,
      });
    } else {
      actions.updateDataMessage(
        getState().dataMessage as Subject<DataMessage[]>
      );
    }
  } else {
    actions.updateDataMessage(getState().dataMessage as Subject<DataMessage[]>);
  }

  actions.setChannelsResolved(true);

  maybeFlipAutoAfterFirstFrame(
    actions,
    getState,
    chosenMode,
    chosenPalette,
    rgbChannels
  );
}

// TODO (dirty state for autolv/NS)
export const model: VizSceneStateModel = {
  ...defaultState,
  ...vizErrorModel,
  _setMetadata: action((state, metadata) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    state.metadata = metadata; // metadata is now SensorMetadata[]
  }),

  updateSensorLuts: action((state, sensorLuts) => {
    state.sensorLuts = sensorLuts;
  }),
  _setPcColorers: action((state, pcColorers) => {
    state.pcColorers = pcColorers;
  }),

  _setSelectedImagePanelSn: action((state, sn) => {
    state.twoDImageSettings.selectedImagePanelSn = sn;
  }),

  setAvailableChannels: action((state, channels) => {
    state.availableChannels = sortChannels(channels);
  }),

  setChannelsResolved: action((state, resolved) => {
    state.channelsResolved = resolved;
  }),

  setRgbChannels: action((state, channels) => {
    state.rgbChannels = channels.sort();
  }),

  currentSensorImageSettings: computed(
    // State dependencies for memoization
    [
      (state) => state.twoDImageSettings.imageSettings,
      (state) => state.twoDImageSettings.selectedImagePanelSn,
    ],
    (imageSettings, selectedImagePanelSn): ImageSettings[] => {
      if (
        selectedImagePanelSn &&
        imageSettings &&
        imageSettings[selectedImagePanelSn]
      ) {
        return imageSettings[selectedImagePanelSn];
      }
      return []; // Return empty array if no sensor selected or settings don't exist
    }
  ),

  updateMetadata: thunk((actions, sensorsMetadata: any[], { getState }) => {
    clearImageColorerCache();
    // New recording: reset auto-exposure EMA so the first frame is warmed up
    // rather than starting from stale or zero history.
    resetRgbAutoExposure();

    if (!sensorsMetadata || sensorsMetadata.length === 0) {
      return;
    }

    actions.setChannelsResolved(false);

    const { coloringMode, colorPalette } = getState().pointCloudGlobalSettings;
    const { rgbChannels } = getState();

    // Create colorers for each sensor
    const newPcColorers: Record<string, PointcloudColorer> = {};
    const isRgbMode = isRgbColorMode({
      coloringMode,
      rgbChannels,
    });
    sensorsMetadata.forEach((metadata) => {
      const sn = resolveMetadataSensorKey(metadata);
      newPcColorers[sn] = new PointcloudColorer(
        coloringMode,
        allColorPalettes[colorPalette],
        isRgbMode
      );
    });
    actions._setPcColorers(newPcColorers);
    actions._setLastUserImageColorModeBySensorIdx({});

    const allPcSensorsSettings: PointCloudSensorsSettings = {};
    const sensorSerialNumbers: string[] = [];
    const initialImageSettings: Record<string, ImageSettings[]> = {};

    sensorsMetadata.forEach((metadata) => {
      const sn = resolveMetadataSensorKey(metadata);
      sensorSerialNumbers.push(sn); // Collect serial numbers

      const sensorChannels = resolveSensorChannelsForReturns({
        sensorKey: sn,
        state: getState(),
        fallbackAvailableChannels: getState().availableChannels,
      });

      // Get settings specific to this sensor
      const sensorSettings = getPointCloudSensorsSettings(
        resolvePointReturnsModeForSensor({
          coloringMode,
          sensorChannels,
          rgbChannels,
        }),
        sn,
        sensorChannels
      );

      if (sensorSettings && sensorSettings[sn]) {
        allPcSensorsSettings[sn] = sensorSettings[sn];
      } else {
        console.warn(`Could not get point cloud settings for sensor SN: ${sn}`);
      }

      initialImageSettings[sn] = [
        JSON.parse(JSON.stringify(defaultImageSettings)),
      ];
    });

    const initialSelectedSn =
      sensorSerialNumbers.length > 0 ? sensorSerialNumbers[0] : null;
    actions.setSelectedImagePanelSn(initialSelectedSn);

    // Update the state with the settings for all sensors
    actions._setPointCloudSensorsSettings(allPcSensorsSettings);
    actions._setMetadata(sensorsMetadata); // Store full metadata array

    cachedSensorReturnNumbers = {};
    cachedColorerReturns = {};
    sensorSerialNumbers.forEach((sn) => {
      const sensorSettings = allPcSensorsSettings[sn]?.returnsSettings;
      const returnNumbers: number[] = [];
      const colorerReturns: ReturnColor[] = [];
      if (sensorSettings?.length > 0) {
        sensorSettings.forEach((setting, index) => {
          if (setting.visible) {
            returnNumbers.push(index);
            colorerReturns.push({
              index,
              highlightColor: setting.highlightColor ?? null,
            });
          }
        });
      }
      cachedSensorReturnNumbers[sn] = returnNumbers;
      cachedColorerReturns[sn] = colorerReturns;
    });
    // --- Global Settings (Color Modes, etc.) ---
    // Decision: use UNION across all sensor profiles for global pointcloud mode options.
    // Rationale: with multi-sensor scenes we prefer exposing all available coloring modes;
    // sensors missing the selected channel are handled by downstream draw guards.
    const availablePointColorModes =
      resolveAvailablePointColorModesUnionForMetadata(sensorsMetadata);
    actions.setAvailablePointColorModes(availablePointColorModes);

    const selectedSensorImageColorModes =
      resolveAvailableImageColorModesForSelectedSensor({
        selectedSn: initialSelectedSn,
        sensorsMetadata,
      });
    actions.setAvailableImageColorModes(selectedSensorImageColorModes);

    if (!selectedSensorImageColorModes.includes(COLOR_MODE.SIGNAL)) {
      // Iterate through the initialized image settings for each sensor
      Object.keys(initialImageSettings).forEach((sn) => {
        const sensorImageSettings = initialImageSettings[sn];
        // Check if the first (default) image setting for this sensor is SIGNAL
        if (
          sensorImageSettings.length === 1 &&
          sensorImageSettings[0].coloringMode === SIGNAL_MODE
        ) {
          sensorImageSettings[0].coloringMode = COLOR_MODE.REFLECTIVITY;
        }
      });
    }

    // Initialize 2D Image Settings for ALL sensors
    const imageInfoMap: TwoDImageState['imageInfo'] = {};
    sensorsMetadata.forEach((meta) => {
      const sn = resolveMetadataSensorKey(meta);
      const df = meta?.dataFormat;
      // beamAltitudeDegrees is deliberately not required here: the pixel
      // dimensions alone are enough to show the panel, and imageAspectRatio
      // falls back to a default ratio when the beam intrinsics are unusable.
      // Requiring it dropped sensors from imageInfo entirely, which is what
      // left recordings with damaged intrinsics showing no 2D image at all.
      if (sn && df?.columnsPerFrame && df?.pixelsPerColumn) {
        imageInfoMap[sn] = {
          dimensions: [df.columnsPerFrame, df.pixelsPerColumn],
          aspectRatio: imageAspectRatio(meta?.beamAltitudeDegrees),
        };
      }
    });
    actions.updateTwoDImageSettings({
      imageSettings: initialImageSettings, // Set the initialized settings record
      imageInfo: imageInfoMap,
      selectedImagePanelSn: initialSelectedSn, // Ensure selected SN is part of the update
    });
  }),

  _setPointCloudGlobalSettings: action((state, newSettings) => {
    const oldPointCloudGlobalSettings = { ...state.pointCloudGlobalSettings };
    const { rgbChannels } = state;
    if (Object.keys(newSettings).length > 1)
      throw new Error('Trying to set too many point cloud settings at once');
    const currentMode = oldPointCloudGlobalSettings.coloringMode;
    const currentPalette = oldPointCloudGlobalSettings.colorPalette;
    if (newSettings.coloringMode) {
      const { coloringMode } = newSettings;

      oldPointCloudGlobalSettings.coloringMode = coloringMode;
      syncOperatorsToColorMode({
        state,
        oldPointCloudGlobalSettings,
        coloringMode,
        currentMode,
        currentPalette,
        rgbChannels,
      });
      // update sensor settings
    } else if (newSettings.colorPalette) {
      if (rgbChannels && rgbChannels.includes(currentMode)) {
        const userPref = state.lastUserPointCloudAutoLevelsOn;
        state.pcAutoLeveler.isActive = false;
        state.pcScaler.isActive = false;
        oldPointCloudGlobalSettings.isAutoLevelsDisabled = false;
        oldPointCloudGlobalSettings.isAutoLevelsOn = userPref ?? true;
      }

      const newPalette = newSettings.colorPalette;
      syncOperatorsToColorPalette({
        state,
        oldPointCloudGlobalSettings,
        currentMode,
        newPalette,
      });
    } else if (!isUndefined(newSettings.isAutoLevelsOn)) {
      oldPointCloudGlobalSettings.isAutoLevelsOn = newSettings.isAutoLevelsOn;
      const isRgbMode = isRgbColorMode({
        coloringMode: oldPointCloudGlobalSettings.coloringMode,
        rgbChannels,
      });
      state.pcAutoLeveler.isActive = !isRgbMode && newSettings.isAutoLevelsOn;
      state.pcScaler.isActive = !isRgbMode && !newSettings.isAutoLevelsOn;

      // only persist as user preference if autolevels are not forced-off by current mode/palette
      const mode = oldPointCloudGlobalSettings.coloringMode;
      const palette = oldPointCloudGlobalSettings.colorPalette;

      const isForcedOff = shouldDisableAutoLevelsForMode({
        coloringMode: mode,
        colorPalette: palette,
        rgbChannels,
      });

      if (!isForcedOff) {
        state.lastUserPointCloudAutoLevelsOn = newSettings.isAutoLevelsOn;
      }
    } else if (!isUndefined(newSettings.isAutoLevelsDisabled)) {
      oldPointCloudGlobalSettings.isAutoLevelsDisabled =
        newSettings.isAutoLevelsDisabled;

      if (newSettings.isAutoLevelsDisabled) {
        oldPointCloudGlobalSettings.isAutoLevelsOn = false;
        state.pcAutoLeveler.isActive = false;

        const isRgb = isRgbColorMode({
          coloringMode: currentMode,
          rgbChannels,
        });
        if (isRgb) state.pcScaler.isActive = false;
      }
    } else if (!isUndefined(newSettings.isNoiseSuppressionOn)) {
      oldPointCloudGlobalSettings.isNoiseSuppressionOn =
        newSettings.isNoiseSuppressionOn;
      state.pcNoiseSuppressor.isActive = newSettings.isNoiseSuppressionOn;

      if (oldPointCloudGlobalSettings.coloringMode === NEAR_IR_MODE) {
        state.lastUserPointCloudNoiseSuppressionOn =
          newSettings.isNoiseSuppressionOn;
      }
    } else if (!isUndefined(newSettings.showPointCloud)) {
      oldPointCloudGlobalSettings.showPointCloud = newSettings.showPointCloud;
    } else if (!isUndefined(newSettings.pointSize)) {
      oldPointCloudGlobalSettings.pointSize = Math.min(
        Math.max(newSettings.pointSize, MIN_POINT_SIZE),
        MAX_POINT_SIZE
      );
    } else if (!isUndefined(newSettings.showRangeRings)) {
      oldPointCloudGlobalSettings.showRangeRings = newSettings.showRangeRings;
    } else if (newSettings.colorModeOptions) {
      const validColorModes = newSettings.colorModeOptions;
      oldPointCloudGlobalSettings.colorModeOptions = validColorModes;
    } else if (newSettings.colorPaletteOptions) {
      oldPointCloudGlobalSettings.colorPaletteOptions =
        newSettings.colorPaletteOptions;
    } else if (!isUndefined(newSettings.isOrthographicCamera)) {
      oldPointCloudGlobalSettings.isOrthographicCamera =
        newSettings.isOrthographicCamera;
    }

    state.pointCloudGlobalSettings = oldPointCloudGlobalSettings;
  }),

  updatePointCloudGlobalSettings: thunk(
    (actions, newSettings, { getState }) => {
      // Set PointCloudGlobalSettings object, we don't use this in the DA directly atm
      // _setPointCloudGlobalSettings throws if more than one setting is passed at once
      actions._setPointCloudGlobalSettings(newSettings);

      // Track if we need to recreate observables
      let needsObservableRecreation = false;

      if (newSettings.coloringMode) {
        needsObservableRecreation = true;
        const nextColoringMode = newSettings.coloringMode;

        const { metadata, availableChannels } = getState(); // metadata is SensorMetadata[]
        if (!Array.isArray(metadata) || metadata.length === 0) return;

        const allPcSensorsSettings: PointCloudSensorsSettings = {};
        metadata.forEach((sensorMeta) => {
          const sn = resolveMetadataSensorKey(sensorMeta);
          const prevSensorSettings =
            getState().pointCloudSensorsSettings[sn]?.returnsSettings ?? [];
          const prevSensorVisibility =
            getState().pointCloudSensorsSettings[sn]?.sensorVisibility;
          const sensorChannels = resolveSensorChannelsForReturns({
            sensorKey: sn,
            state: getState(),
            fallbackAvailableChannels: availableChannels,
          });
          const sensorSettings = getPointCloudSensorsSettings(
            resolvePointReturnsModeForSensor({
              coloringMode: nextColoringMode,
              sensorChannels,
              rgbChannels: getState().rgbChannels,
            }),
            sn,
            sensorChannels
          );
          if (sensorSettings && sensorSettings[sn]) {
            const mergedReturnsSettings =
              sensorSettings[sn].returnsSettings?.map((setting, index) => ({
                ...setting,
                visible: prevSensorSettings[index]?.visible ?? setting.visible,
                highlightColor: null,
              })) ?? [];

            allPcSensorsSettings[sn] = {
              ...sensorSettings[sn],
              sensorVisibility:
                prevSensorVisibility ?? sensorSettings[sn].sensorVisibility,
              returnsSettings: mergedReturnsSettings,
            };

            // Reset highlight colors while preserving user-selected return visibility.
            cachedColorerReturns[sn] = mergedReturnsSettings
              .map((setting, index) => ({ setting, index }))
              .filter(({ setting }) => setting.visible)
              .map(({ index }) => ({
                index,
                highlightColor: null,
              }));
          }
        });
        actions._setPointCloudSensorsSettings(allPcSensorsSettings);
      } else if (newSettings.colorPalette) {
        needsObservableRecreation = true;
        // Reset cached colorer's highlight colors to null when coloring palette changes
        const sensorsSettings = { ...getState().pointCloudSensorsSettings };
        Object.keys(sensorsSettings).forEach((sn) => {
          if (sensorsSettings[sn].returnsSettings) {
            sensorsSettings[sn].returnsSettings = sensorsSettings[
              sn
            ].returnsSettings.map((setting) => ({
              ...setting,
              highlightColor: null,
            }));
          }
          cachedColorerReturns[sn] = (
            sensorsSettings[sn].returnsSettings ?? []
          ).map((setting, index) => ({ index, highlightColor: null }));
        });
        const currentState = getState();
        Object.values(currentState.pcColorers).forEach((colorer) => {
          colorer.colorMap =
            allColorPalettes[
              newSettings.colorPalette as keyof typeof allColorPalettes
            ];
        });
      } else if (!isUndefined(newSettings.isAutoLevelsOn)) {
        // pcAutoLeveler is captured by reference in the running pipeline closure
        // (see updateDataMessage/transformPoints). It can be reassigned elsewhere
        // (e.g. initializePointCloudDefaults) without a matching resync, so force
        // a full recreateObservables() rather than just replaying the cached frame.
        needsObservableRecreation = true;
      } else if (!isUndefined(newSettings.isAutoLevelsDisabled)) {
        needsObservableRecreation = true;
      } else if (!isUndefined(newSettings.isNoiseSuppressionOn)) {
        needsObservableRecreation = true;
      } else if (!isUndefined(newSettings.pointSize)) {
        actions.setPointSize(newSettings.pointSize);
      } else if (!isUndefined(newSettings.showRangeRings)) {
        actions.setAreRangeRingsVisible(newSettings.showRangeRings);
      } else if (newSettings.colorModeOptions) {
        actions.setAvailablePointColorModes(newSettings.colorModeOptions);
      } else if (newSettings.colorPaletteOptions) {
        actions.setColorPalettes(newSettings.colorPaletteOptions);
      } else if (!isUndefined(newSettings.isOrthographicCamera)) {
        // ORTHO ACTION
        actions.setIsCameraOrthographic(newSettings.isOrthographicCamera); // Added action call
      }

      // Recreate observables if operators were updated
      if (needsObservableRecreation) {
        actions.recreateObservables();
      }
    }
  ),

  togglePointCloudVisibility: thunk((actions, _, { getState }) => {
    const vis = getState().pointCloudGlobalSettings.showPointCloud;
    actions.updatePointCloudGlobalSettings({ showPointCloud: !vis });
    actions.recreateObservables();
    return !vis;
  }),

  _setPointCloudSensorsSettings: action((state, settings) => {
    state.pointCloudSensorsSettings = settings;
  }),

  updatePointCloudSensorsSettings: thunk(
    (actions, [sn, newSettings], { getState }) => {
      const oldSensorsSettings = getState().pointCloudSensorsSettings;
      // Create a new object for the updated settings
      const newSensorsSettings = {
        ...oldSensorsSettings,
        [sn]: {
          ...(oldSensorsSettings[sn] ?? {}),
          ...newSettings,
        },
      };
      actions._setPointCloudSensorsSettings(newSensorsSettings);

      // --- Update Cache for the modified sensor ---
      const updatedSensorSettings = newSensorsSettings[sn]?.returnsSettings;
      const newReturnNumbers: number[] = [];
      const newColorerReturns: ReturnColor[] = [];
      let hasReturnsChange = false;
      if (updatedSensorSettings?.length > 0) {
        hasReturnsChange = true;
        updatedSensorSettings.forEach((setting, index) => {
          if (setting.visible) {
            newReturnNumbers.push(index);
            newColorerReturns.push({
              index,
              highlightColor: setting.highlightColor ?? null,
            });
          }
        });
      }
      cachedSensorReturnNumbers[sn] = newReturnNumbers;
      cachedColorerReturns[sn] = newColorerReturns;

      // Recreate observables if returns settings or visibility changed
      if (hasReturnsChange || newSettings.sensorVisibility !== undefined) {
        actions.recreateObservables();
      }
    }
  ),

  _setDataMessage: action((state, dataMessage) => {
    state.dataMessage = dataMessage;
  }),
  _setPointCloudObservable: action((state, obs) => {
    state.pointCloudObservable = obs;
  }),
  _setImageObservable: action((state, obs) => {
    state.imageObservable = obs;
  }),

  // Recreate observables using the existing dataMessage subject
  // This is needed when operators change but we want to keep the same subject
  recreateObservables: thunk((actions, _, { getState }) => {
    const state = getState();
    const currentDataMessage = state.dataMessage as Subject<DataMessage[]>;

    if (currentDataMessage) {
      // Recreate the observables with updated operators
      actions.updateDataMessage(currentDataMessage);

      const lastData = frameCache.lastDataMessage;

      if (
        state.shouldReplayCachedFrameOnRecreate &&
        lastData &&
        lastData.length > 0
      ) {
        // Replay the latest cached frame so rebuilt pipelines render immediately.
        setTimeout(() => {
          const latestState = getState();
          const latestDataMessage = latestState.dataMessage as Subject<
            DataMessage[]
          >;
          const latestLastData = frameCache.lastDataMessage;

          if (
            latestState.shouldReplayCachedFrameOnRecreate &&
            latestLastData &&
            latestLastData.length > 0
          ) {
            latestDataMessage.next(latestLastData);
          }
        }, 0);
      }
    }
  }),

  updateDataMessage: thunk((actions, datamessage, { getState }) => {
    const { _setDataMessage, _setPointCloudObservable, _setImageObservable } =
      actions;
    _setDataMessage(datamessage); // Store the raw subject

    const {
      pointCloudGlobalSettings: { coloringMode },
      rgbChannels,
      currentSensorImageSettings,
    } = getState();
    const shouldPreloadRgbForPointCloud = isRgbColorMode({
      coloringMode,
      rgbChannels,
    });
    const shouldPreloadRgbForImage = currentSensorImageSettings.some(
      (setting) =>
        isRgbColorMode({
          coloringMode: setting.coloringMode,
          rgbChannels,
        })
    );
    if (shouldPreloadRgbForPointCloud || shouldPreloadRgbForImage) {
      preloadRgbAutoExposureSdk()
        .then(() => {
          if (shouldPreloadRgbForPointCloud) {
            reprocessPointCloudFrame(
              getState().pointCloudReprocessed$ as Subject<DataMessage[]>
            );
          }

          if (shouldPreloadRgbForImage) {
            const latestLastData = frameCache.lastDataMessage;
            if (latestLastData && latestLastData.length > 0) {
              // If the viewer is paused, the first frame may have arrived before
              // RGB image processing finished initializing. Replay the cached
              // frame once the SDK is ready so the image pipeline gets one
              // intentional post-init render instead of waiting for playback.
              datamessage.next(latestLastData);
            }
          }
        })
        .catch((error) => {
          console.error('Failed to preload RGB auto exposure SDK:', error);
        });
    }

    // Get operators - they are configured elsewhere now (updateMetadata, updatePointCloudSensorsSettings)
    const {
      imageDestaggerer,
      pcNoiseSuppressor,
      pcScaler,
      pcAutoLeveler,
      pointCloudReprocessed$,
    } = getState();

    const getSensorColorerForMessage = (
      sensorSn: string
    ): PointcloudColorer => {
      const currentState = getState();
      const lookupSn = resolvePointcloudLookupKey({
        messageSn: sensorSn,
        state: currentState,
      });
      if (!currentState.pcColorers[lookupSn]) {
        const currentGlobalSettings = currentState.pointCloudGlobalSettings;
        const currentRgbChannels = currentState.rgbChannels;
        const isRgbMode = isRgbColorMode({
          coloringMode: currentGlobalSettings.coloringMode,
          rgbChannels: currentRgbChannels,
        });
        // Create new colorer for this sensor
        const newColorer = new PointcloudColorer(
          currentGlobalSettings.coloringMode,
          allColorPalettes[currentGlobalSettings.colorPalette],
          isRgbMode
        );

        // Update the state properly using the action
        const updatedColorers = {
          ...currentState.pcColorers,
          [lookupSn]: newColorer,
        };
        actions._setPcColorers(updatedColorers);

        return newColorer;
      }
      return currentState.pcColorers[lookupSn];
    };

    // --- Define transformPoints ---
    const transformPoints = (msg: PointsDataMessage): RangeRGBData[] => {
      try {
        const sensorSn = msg?.sensorInfo?.sn;
        if (!sensorSn) {
          // console.warn('Message missing sensor SN in transformPoints', msg);
          return [];
        }

        const lookupSn = resolvePointcloudLookupKey({
          messageSn: sensorSn,
          state: getState(),
        });

        const sensorSpecificReturnNumbers =
          cachedSensorReturnNumbers[lookupSn] ?? [];
        const colorerWhichReturns = cachedColorerReturns[lookupSn] ?? [];

        pcNoiseSuppressor.whichReturns = sensorSpecificReturnNumbers;
        pcScaler.whichReturns = sensorSpecificReturnNumbers;
        pcAutoLeveler.whichReturns = sensorSpecificReturnNumbers;

        const {
          pointCloudGlobalSettings: currentGlobalSettings,
          rgbChannels: currentRgbChannels,
        } = getState();
        const currentColorMode = currentGlobalSettings.coloringMode;
        const isRgbMode = isRgbColorMode({
          coloringMode: currentColorMode,
          rgbChannels: currentRgbChannels,
        });
        const mutableMsg = cloneMutablePointCloudChannels(
          msg,
          currentColorMode,
          sensorSpecificReturnNumbers,
          isRgbMode
        );
        const shouldScale =
          !isRgbMode &&
          !isReflectivityColorMode(currentColorMode) &&
          !isWindowColorMode(currentColorMode) &&
          !currentGlobalSettings.isAutoLevelsOn;
        pcScaler.isActive = shouldScale;

        const suppressed =
          pcNoiseSuppressor.suppressPointcloudNoise(mutableMsg);
        const scaled = pcScaler.scalePointcloud(suppressed);
        const autoLeveled = pcAutoLeveler.autoLevelPointcloud(scaled);
        if (autoLeveled === null) return [];

        const sensorColorer = getSensorColorerForMessage(sensorSn);
        sensorColorer.whichReturns = colorerWhichReturns;

        // Use the sensor-specific colorer
        const colored = sensorColorer.colorPointcloud(autoLeveled);

        const lut = getState().sensorLuts[sensorSn];
        if (lut) {
          colored.forEach((point) => {
            point.lut = lut;
          });
        }

        return colored.filter(
          (point) => point.rangeArray && point.rangeArray.length > 0
        );
      } catch (e) {
        console.error(
          `Error in transformPoints for sensor ${msg?.sensorInfo?.sn}:`,
          e
        );
        return [];
      }
    };

    const processPointCloudBatch = (msgs: DataMessage[]) =>
      from(msgs).pipe(
        filter((msg) => {
          const currentState = getState();
          const sensorSettings = currentState.pointCloudSensorsSettings;
          const sn = msg?.sensorInfo?.sn;
          const lookupSn = sn
            ? resolvePointcloudLookupKey({
                messageSn: sn,
                state: currentState,
              })
            : undefined;
          const setting = lookupSn ? sensorSettings[lookupSn] : undefined;
          // If sensor setting exists and sensorVisibility is false, filter out.
          return setting ? setting.sensorVisibility !== false : true;
        }),
        mergeMap((msg: DataMessage) => {
          const { pointCloudGlobalSettings } = getState();
          return from([
            applyPointCloudRgbAutoExposure(
              msg,
              pointCloudGlobalSettings.coloringMode,
              pointCloudGlobalSettings.isAutoLevelsOn
            ),
          ]).pipe(
            operators.convertDataMessageForPointcloud(),
            map(transformPoints),
            catchError((err) => {
              console.error('Error in processPointCloud pipeline:', err);
              return of([]);
            })
          );
        }),
        toArray() // Collect all processed results into an array
      );

    let latestPointCloudBatchId = 0;

    const pcObservable = merge(datamessage, pointCloudReprocessed$).pipe(
      mergeMap((msgs: DataMessage[]) => {
        latestPointCloudBatchId += 1;
        const batchId = latestPointCloudBatchId;
        return processPointCloudBatch(msgs).pipe(
          map((processedMsgs) => ({ batchId, processedMsgs }))
        );
      }),
      filter(({ batchId }) => batchId === latestPointCloudBatchId),
      map(({ processedMsgs }) => processedMsgs),
      // use refCount so replaced observables unsubscribe upstream.
      // Otherwise repeated recreateObservables() calls leave old pipelines alive.
      // When the reference count drops to zero (no active subscribers), the shared
      // subscription to `datamessage` is torn down and upstream processing stops.
      // Any subsequent subscribers will trigger a new upstream subscription and
      // replay only from the new emissions buffered (bufferSize: 1), not from
      // values produced while there were no subscribers.
      shareReplay({ bufferSize: 1, refCount: true })
    );

    /*
      In below, we optimize the image processing by chaining the image operators
      together and only processing the image once.
      Also, we clean up the pipeline, so it doesn't share twice and reconnect every time.
      To make sure the image is updated even on pause, we have the useEffect on DriveViewer, so when the image setting change, the message will be resent and the pipeline will be reconnected.
    */
    const processImage = (msg: DataMessage) =>
      imageDestaggerer.mapImage(copyDataMessage(msg));

    const imgObservable = datamessage.pipe(
      map((msgs: DataMessage[]) => {
        const selectedSn = getState().twoDImageSettings.selectedImagePanelSn;
        const metadata = getState().metadata as SDKSensorInfoAugmented[];
        return findMessageForSelectedSensor({
          messages: msgs,
          selectedSn,
          sensorsMetadata: Array.isArray(metadata) ? metadata : [],
        });
      }),
      filter((msg): msg is DataMessage => Boolean(msg)),
      // auditTime rather than observeOn: observeOn delivers every message and
      // never drops, so once copy + destagger per image costs more than the
      // frame interval, 2D falls further and further behind the point cloud
      // (which already keeps only its latest batch above). auditTime keeps the
      // newest message per animation frame, so a slow device shows 2D at a
      // lower rate but in sync. A lone message is still delivered on the
      // trailing edge, so pause and settings-change resends lose nothing.
      auditTime(0, animationFrameScheduler),
      map(processImage),
      share()
    );

    _setPointCloudObservable(pcObservable);
    _setImageObservable(imgObservable);
  }),
  ...vizCameraModel,

  incrementPointSize: thunk((actions, _, { getState }) => {
    const { pointSize } = getState();

    const newPointSize = Math.min(
      pointSize + POINT_SIZE_INCREMENT,
      MAX_POINT_SIZE
    );

    actions.updatePointCloudGlobalSettings({ pointSize: newPointSize });

    return getState().pointSize;
  }),

  decrementPointSize: thunk((actions, _, { getState }) => {
    const { pointSize } = getState();

    const newPointSize = Math.max(
      pointSize - POINT_SIZE_INCREMENT,
      MIN_POINT_SIZE
    );
    actions.updatePointCloudGlobalSettings({ pointSize: newPointSize });

    return getState().pointSize;
  }),

  // TODO (mw) ensure point size max mins are shared btw left sidebar and data app min/max consts
  setPointSize: action((state, requestedPointSize) => {
    const newPointSize = Math.min(
      Math.max(requestedPointSize, MIN_POINT_SIZE),
      MAX_POINT_SIZE
    );

    if (
      requestedPointSize < MIN_POINT_SIZE ||
      requestedPointSize > MAX_POINT_SIZE
    ) {
      console.warn(
        `New requested point size was ${requestedPointSize} ` +
          `but the size has to be within [${MIN_POINT_SIZE},` +
          `${MAX_POINT_SIZE}]. Setting it to ${newPointSize}.`
      );
    }

    state.pointSize = newPointSize;
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // Color Modes
  /// ///////////////////////////////////////////////////////////////////////////
  setAvailablePointColorModes: thunk((actions, modes, { getState }) => {
    // Update available modes in store
    actions._setAvailablePointColorModes(modes);
    actions._setPointCloudGlobalSettings({ colorModeOptions: modes });

    // Update current pointcloud mode if needed.
    const currentPointColorMode = selectEffectivePointCloudColorMode(
      getState()
    );
    if (
      !currentPointColorMode ||
      !getState().availablePointColorModes.includes(currentPointColorMode)
    ) {
      actions.setDefaultPointColorMode();
    }
  }),

  setAvailableImageColorModes: thunk((actions, modes, { getState }) => {
    // Update available modes in store
    actions._setAvailableImageColorModes(modes);

    // Update current imageColorMode if needed
    const state = getState();
    const currentImageColorMode = selectEffectiveImageColorMode(state);
    if (
      !currentImageColorMode ||
      !state.availableImageColorModes.includes(currentImageColorMode)
    ) {
      actions.setDefaultImageColorMode();
    }
  }),

  _setAvailablePointColorModes: action((state, modes) => {
    state.availablePointColorModes = modes;
  }),

  _setAvailableImageColorModes: action((state, modes) => {
    state.availableImageColorModes = modes;
  }),

  setDefaultPointColorMode: thunk((actions, _, { getState }) => {
    const availableModes = [...getState().availablePointColorModes];

    const defaultMode = extractDefaultColorSetting(
      availableModes,
      DEFAULT_POINT_COLOR_MODES,
      'No available point color modes yet'
    );

    actions.updatePointCloudGlobalSettings({ coloringMode: defaultMode });
  }),

  setDefaultImageColorMode: thunk((actions, _, { getState }) => {
    const availableModes = [...getState().availableImageColorModes];

    const defaultMode = extractDefaultColorSetting(
      availableModes,
      DEFAULT_IMAGE_COLOR_MODES,
      'No available image color modes yet'
    );

    const preChangeState = getState();
    const payload = buildSetImageColorModeUpdatePayload(
      preChangeState,
      defaultMode
    );
    if (!payload) {
      return selectEffectiveImageColorMode(getState());
    }

    actions.updateImage({
      ...payload,
      markColorModeDirty: false,
    });

    return selectEffectiveImageColorMode(getState());
  }),

  setPointColorMode: thunk((actions, newPointColorMode, { getState }) => {
    const state = getState();
    if (!state.availablePointColorModes.includes(newPointColorMode)) {
      throw new Error(
        `Color mode ${newPointColorMode} is not valid for this data`
      );
    }
    actions.updatePointCloudGlobalSettings({
      coloringMode: newPointColorMode,
    });

    return selectEffectivePointCloudColorMode(getState());
  }),

  setImageColorMode: thunk((actions, newImageColorMode, { getState }) => {
    const preChangeState = getState();

    // Canonical write path: update the selected panel's primary image settings.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const payload = buildSetImageColorModeUpdatePayload(
      preChangeState,
      newImageColorMode
    );
    if (!payload) {
      return selectEffectiveImageColorMode(getState());
    }

    actions.updateImage({
      ...payload,
      markColorModeDirty: true,
    });

    return selectEffectiveImageColorMode(getState());
  }),

  togglePointCloudAutoLevel: thunk((actions, _, { getState }) => {
    const { pointCloudGlobalSettings, rgbChannels } = getState();
    const { coloringMode: pointColorMode, colorPalette: pointColorPalette } =
      pointCloudGlobalSettings;

    if (
      pointColorMode &&
      shouldDisableAutoLevelsForMode({
        coloringMode: pointColorMode,
        colorPalette: pointColorPalette,
        rgbChannels,
      })
    ) {
      throw new Error(
        `Auto-levels is disabled when coloring by ${pointColorMode} with ${pointColorPalette} palette`
      );
    }

    const wasPointCloudAutoLevel =
      getState().pointCloudGlobalSettings.isAutoLevelsOn;

    actions.updatePointCloudGlobalSettings({
      isAutoLevelsOn: !wasPointCloudAutoLevel,
    });
    return selectEffectivePointCloudAutoLevelsOn(getState());
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // Color Palettes
  /// ///////////////////////////////////////////////////////////////////////////
  setColorPalettes: thunk((actions, palettes, { getState }) => {
    const state = getState();
    // Update palettes array in store state
    actions._setColorPalettes(palettes);
    actions._setPointCloudGlobalSettings({
      colorPaletteOptions: palettes as ColorPaletteName[],
    });

    // Update current point and image palettes if needed.
    // Use `palettes` (the incoming list) rather than `state.colorPalettes`
    // (the stale snapshot captured before _setColorPalettes ran) so that a
    // palette that IS in the new list is never incorrectly reset to the default.
    const currentImageColorPalette = selectEffectiveImageColorPalette(state);
    const currentPointColorPalette =
      selectEffectivePointCloudColorPalette(state);
    if (
      !currentImageColorPalette ||
      !palettes.includes(currentImageColorPalette)
    ) {
      actions.setDefaultImageColorPalette();
    }
    if (
      !currentPointColorPalette ||
      !palettes.includes(currentPointColorPalette)
    ) {
      actions.setDefaultPointColorPalette();
    }
  }),

  _setColorPalettes: action((state, palettes) => {
    state.colorPalettes = palettes;
  }),

  setDefaultPointColorPalette: thunk((actions, _, { getState }) => {
    const palettes = [
      ...getState().pointCloudGlobalSettings.colorPaletteOptions,
    ];

    const defaultPalette = extractDefaultColorSetting(
      palettes,
      DEFAULT_POINT_COLOR_PALETTES,
      'No available color palettes yet'
    );

    actions.setPointColorPalette(defaultPalette);
  }),

  // Since we have multiple images, cycling doesn't make a ton of sense
  setDefaultImageColorPalette: thunk((actions, _, { getState }) => {
    const palettes = [...getState().colorPalettes];

    const defaultPalette = extractDefaultColorSetting(
      palettes,
      DEFAULT_IMAGE_COLOR_PALETTES,
      'No available color palettes yet'
    );

    actions.setImageColorPalette(defaultPalette);
  }),

  setPointColorPalette: thunk((actions, palette, { getState }) => {
    actions.updatePointCloudGlobalSettings({
      colorPalette: palette as ColorPaletteName,
    });

    return selectEffectivePointCloudColorPalette(getState());
  }),

  setImageColorPalette: thunk((actions, palette, { getState }) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const payload = buildSetImageColorPaletteUpdatePayload(
      getState(),
      palette as ColorPaletteName
    );

    if (payload) {
      actions.updateImage(payload);
    }

    return selectEffectiveImageColorPalette(getState());
  }),

  _setLastUserImagePaletteBySensorIdxAndMode: action((state, next) => {
    state.lastUserImagePaletteBySensorIdxAndMode = next;
  }),
  _setLastUserImageColorModeBySensorIdx: action((state, next) => {
    state.lastUserImageColorModeBySensorIdx = next;
  }),
  _setLastUserImageAutoLevelsBySensorIdx: action((state, next) => {
    state.lastUserImageAutoLevelsBySensorIdx = next;
  }),
  _setLastUserImageNoiseSuppressionBySensorIdx: action((state, next) => {
    state.lastUserImageNoiseSuppressionBySensorIdx = next;
  }),
  ...vizRendererModel,

  initAvailableChannels: thunk((actions, _, { getState }) => {
    const { imageObservable } = getState();
    const sub = imageObservable
      .pipe(take(1))
      .subscribe((message: DataMessage) => {
        if (!message) return;
        initializeAvailableChannelsFromMessage(actions, getState, message);
      });
    return () => sub.unsubscribe();
  }),

  setShouldReplayCachedFrameOnRecreate: action((state, shouldReplay) => {
    state.shouldReplayCachedFrameOnRecreate = shouldReplay;
  }),

  _setTwoDImageSettings: action((state, partialNewSettings) => {
    state.twoDImageSettings = mergeTwoDImageSettingsState({
      currentState: state.twoDImageSettings,
      partialNewSettings,
    });
  }),

  updateTwoDImageSettings: thunk(
    (actions, partialNewSettings, { getState }) => {
      const {
        metadata,
        imageDestaggerer,
        twoDImageSettings,
        imageObservable,
        availableImageColorModes,
        lastUserImagePaletteBySensorIdxAndMode,
        lastUserImageColorModeBySensorIdx,
        rgbChannels,
      } = getState();

      if (!Array.isArray(metadata) || metadata.length === 0) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const nextTwoDImageState = buildTwoDImageSettingsRuntimeState({
        currentState: twoDImageSettings,
        partialNewSettings,
        metadata,
        imageDestaggerer,
        imageObservable,
        availableImageColorModes,
        lastUserImagePaletteBySensorIdxAndMode,
        lastUserImageColorModeBySensorIdx,
        rgbChannels,
      });

      actions._setTwoDImageSettings(nextTwoDImageState);

      // Recreate observables to use updated image operators
      actions.recreateObservables();
    }
  ),

  setSelectedImagePanelSn: thunk((actions, sn, { getState }) => {
    actions._setSelectedImagePanelSn(sn);

    if (sn) {
      const metadata = getState().metadata as SDKSensorInfoAugmented[];
      if (Array.isArray(metadata) && metadata.length > 0) {
        const selectedSensorImageColorModes =
          resolveAvailableImageColorModesForSelectedSensor({
            selectedSn: sn,
            sensorsMetadata: metadata,
          });
        actions.setAvailableImageColorModes(selectedSensorImageColorModes);
      }
    }

    actions.updateTwoDImageSettings({});
  }),

  swapImages: thunk((actions, { sensorSn, indexA, indexB }, { getState }) => {
    const { imageSettings } = getState().twoDImageSettings;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const newImageSettingsRecord = buildSwappedImageSettingsRecord({
      imageSettings,
      sensorSn,
      indexA,
      indexB,
    });

    if (!newImageSettingsRecord) {
      const sensorImageSettingsLen = imageSettings[sensorSn]?.length;
      console.error(
        `swapImages: Invalid indices [${indexA}, ${indexB}] for sensor ${sensorSn} with ${sensorImageSettingsLen} settings.`
      );
      return;
    }

    actions._setTwoDImageSettings({ imageSettings: newImageSettingsRecord });

    actions.updateTwoDImageSettings({});
  }),

  addImage: thunk((actions, sensorSn, { getState }) => {
    const {
      metadata,
      availableImageColorModes,
      twoDImageSettings,
      rgbChannels,
    } = getState();
    const { imageSettings } = twoDImageSettings;

    if (!sensorSn || !imageSettings || !imageSettings[sensorSn]) {
      console.warn(`addImage: Settings not found for sensor SN: ${sensorSn}`);
      return;
    }
    if (!Array.isArray(metadata) || metadata.length === 0) {
      console.warn('addImage: Metadata not available.');
      return;
    }

    const targetSensorMetadata = findSensorMetadataByIdentifier(
      metadata as SDKSensorInfoAugmented[],
      sensorSn
    );
    if (!targetSensorMetadata) {
      console.warn(
        `addImage: Metadata not found for target sensor ${sensorSn}.`
      );
      return;
    }

    const udpProfileLidar =
      extractLidarProfileFromMetadata(targetSensorMetadata);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const nextImageSettingsRecord = buildAddedImageSettingsRecord({
      imageSettings,
      sensorSn,
      availableImageColorModes,
      rgbChannels,
      udpProfileLidar,
      twoDImageOptionsToAddBase: twoDImageOptionsToAddBase as Record<
        string,
        Partial<ImageSettings>
      >,
      paletteMap: allColorPalettes,
    });

    if (!nextImageSettingsRecord) {
      console.warn(
        `addImage: No available image color modes to add for sensor ${sensorSn}.`
      );
      return;
    }

    actions._setTwoDImageSettings({ imageSettings: nextImageSettingsRecord });
    actions.updateTwoDImageSettings({});
  }),

  deleteImage: thunk((actions, { sensorSn, index }, { getState }) => {
    const { imageSettings } = getState().twoDImageSettings;

    if (!sensorSn || !imageSettings || !imageSettings[sensorSn]) {
      console.error(
        `deleteImage: Settings not found for sensor SN: ${sensorSn}`
      );
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const nextImageSettingsRecord = buildDeletedImageSettingsRecord({
      imageSettings,
      sensorSn,
      index,
    });

    if (!nextImageSettingsRecord) {
      console.error(
        `deleteImage: Invalid index ${index} for sensor ${sensorSn}`
      );
      return;
    }

    // Update state and trigger recalculations
    actions._setTwoDImageSettings({ imageSettings: nextImageSettingsRecord });
    actions.updateTwoDImageSettings({});
  }),

  updateImage: thunk(
    (
      actions,
      {
        sensorSn,
        index,
        partialSettings,
        preserveExplicitToggles,
        markColorModeDirty,
      },
      { getState }
    ) => {
      const state = getState();
      const { metadata, twoDImageSettings, rgbChannels } = state;
      const { imageSettings } = twoDImageSettings;

      if (!sensorSn || !imageSettings || !imageSettings[sensorSn]) {
        console.error(
          `updateImage: Settings not found for sensor SN: ${sensorSn}`
        );
        return;
      }
      if (!Array.isArray(metadata) || metadata.length === 0) {
        console.warn('updateImage: Metadata not available.');
        return;
      }
      const targetSensorMetadata = findSensorMetadataByIdentifier(
        metadata as SDKSensorInfoAugmented[],
        sensorSn
      );
      if (!targetSensorMetadata) {
        console.warn(
          `updateImage: Metadata not found for target sensor ${sensorSn}.`
        );
        return;
      }

      const udpProfileLidar =
        extractLidarProfileFromMetadata(targetSensorMetadata);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const nextImageStateChanges = buildUpdateImageStateChanges({
        imageSettings,
        sensorSn,
        index,
        partialSettings,
        preserveExplicitToggles,
        rgbChannels,
        udpProfileLidar,
        twoDImageOptionsToAddBase: twoDImageOptionsToAddBase as Record<
          string,
          Partial<ImageSettings>
        >,
        paletteMap: allColorPalettes,
        lastUserImagePaletteBySensorIdxAndMode:
          state.lastUserImagePaletteBySensorIdxAndMode,
        lastUserImageAutoLevelsBySensorIdx:
          state.lastUserImageAutoLevelsBySensorIdx,
        lastUserImageNoiseSuppressionBySensorIdx:
          state.lastUserImageNoiseSuppressionBySensorIdx,
      });

      if (!nextImageStateChanges) {
        console.error(
          `updateImage: Invalid index ${index} for sensor ${sensorSn}`
        );
        return;
      }

      const {
        newImageSettingsRecord,
        nextLastUserImagePaletteBySensorIdxAndMode,
        nextLastUserImageAutoLevelsBySensorIdx,
        nextLastUserImageNoiseSuppressionBySensorIdx,
      } = nextImageStateChanges;

      if (nextLastUserImagePaletteBySensorIdxAndMode) {
        actions._setLastUserImagePaletteBySensorIdxAndMode(
          nextLastUserImagePaletteBySensorIdxAndMode
        );
      }

      const shouldRememberExplicitColorMode =
        markColorModeDirty ?? partialSettings.coloringMode !== undefined;
      if (
        shouldRememberExplicitColorMode &&
        partialSettings.coloringMode !== undefined
      ) {
        actions._setLastUserImageColorModeBySensorIdx({
          ...state.lastUserImageColorModeBySensorIdx,
          [sensorSn]: {
            ...(state.lastUserImageColorModeBySensorIdx[sensorSn] ?? {}),
            [index]: partialSettings.coloringMode,
          },
        });
      }

      if (nextLastUserImageAutoLevelsBySensorIdx) {
        actions._setLastUserImageAutoLevelsBySensorIdx(
          nextLastUserImageAutoLevelsBySensorIdx
        );
      }

      if (nextLastUserImageNoiseSuppressionBySensorIdx) {
        actions._setLastUserImageNoiseSuppressionBySensorIdx(
          nextLastUserImageNoiseSuppressionBySensorIdx
        );
      }

      actions._setTwoDImageSettings({ imageSettings: newImageSettingsRecord });
      actions.updateTwoDImageSettings({
        imageSettings: newImageSettingsRecord,
      });
    }
  ),
};
